import { api } from '@/lib/axios';
import { getDefaultStore } from 'jotai';
import { authUserMetaAtom, isRehydratingAtom } from '@/store/authAtoms';
import { setCsrfToken } from '@/lib/csrf';

const store = getDefaultStore();

interface SessionStatusResponse {
  userName: string;
  userKey: number;
  permissions: string[];
  csrfToken?: string;
}

/**
 * Called once at app init to verify the BFF admin session.
 *
 * If `authUserMetaAtom` is present in localStorage, the user was previously logged in.
 * We ping GET /admin/Authentication/SessionStatus to confirm the BFF session cookie is
 * still valid.
 *
 * On success: refreshes `authUserMetaAtom` with the latest server-returned values.
 * On failure (401 / network error): clears `authUserMetaAtom` — ProtectedRoute redirects to /login.
 */
export function bootstrapAuth(): void {
  const meta = store.get(authUserMetaAtom);
  if (!meta) return;

  // Set rehydrating flag synchronously — before any await — so the first React
  // render (which happens after module evaluation) sees isRehydratingAtom = true.
  store.set(isRehydratingAtom, true);

  api
    .get<SessionStatusResponse>('/admin/Authentication/SessionStatus')
    .then(({ data }) => {
      if (data.csrfToken) setCsrfToken(data.csrfToken);
      store.set(authUserMetaAtom, {
        id: String(data.userKey),
        fullName: data.userName,
        userId: data.userName,
        permissions: data.permissions,
      });
    })
    .catch(() => {
      store.set(authUserMetaAtom, null);
    })
    .finally(() => {
      store.set(isRehydratingAtom, false);
    });
}
